import Values from "values.js";
import ColorContrastChecker from "color-contrast-checker";
import { isContrast } from "../utils/color-helper";

const palette = {
  "#FBC037": {
    shade4: "#985628",
    shade3: "#B1652F",
    shade2: "#CA7336",
    shade1: "#E4823C",
    default: "#FBC037",
    tint1: "#FD9B56",
    tint2: "#FDA669",
    tint3: "#FEB17B",
    tint4: "#fdd987",
    surface: "#FFFCF5",
    border: "#FEEABC",
    pressed: "#FA9810",
    hover: "#F8A92F",
    focus: "#FBC037",
    isLight: true,
  },
};

export const getColorScheme = (base: string = "#01959F") => {
  const defaultPalette = palette[base];
  const generatedPalette = new Values(base);

  const getPerfectContrast = (baseWeight: number, limitWeight: number) => {
    let weight = baseWeight;
    const contrastRatio = 1.05;

    while (
      !isContrast(generatedPalette.tint(weight).hexString(), Colors.white, contrastRatio) &&
      weight > limitWeight
    ) {
      weight--;
    }

    return generatedPalette.tint(weight).hexString();
  };

  if (defaultPalette) return defaultPalette;

  return {
    shade4: generatedPalette.shade(40).hexString(),
    shade3: generatedPalette.shade(30).hexString(),
    shade2: generatedPalette.shade(20).hexString(),
    shade1: generatedPalette.shade(10).hexString(),
    default: base,
    tint1: generatedPalette.tint(10).hexString(),
    tint2: generatedPalette.tint(20).hexString(),
    tint3: generatedPalette.tint(30).hexString(),
    tint4: generatedPalette.tint(40).hexString(),
    surface: getPerfectContrast(95, 50),
    hover: generatedPalette.shade(20).hexString(),
    pressed: generatedPalette.shade(34.5).hexString(),
    border: generatedPalette.shade(29).hexString(),
    focus: `${generatedPalette.shade(15).hexString()}33`,
    isLight: isContrast(base, Colors.neutral.neutral90),
  };
};

const Colors = {
  primary: {
    shade4: "#01595F",
    shade3: "#01686F",
    shade2: "#01777F",
    shade1: "#01868F",
    default: "#01959F",
    tint1: "#1AA0A9",
    tint2: "#34AAB2",
    tint3: "#4DB5BC",
    tint4: "#67BFC5",
    surface: "#F7FEFF",
    hover: "#01777F",
    pressed: "#146166",
    border: "#4DB5BC",
    focus: "#01959F33",
    isLight: false,
  },
  secondary: {
    shade4: "#985628",
    shade3: "#B1652F",
    shade2: "#CA7336",
    shade1: "#E4823C",
    default: "#FBC037",
    tint1: "#FD9B56",
    tint2: "#FDA669",
    tint3: "#FEB17B",
    tint4: "#fdd987",
    surface: "#FFFCF5",
    border: "#FEEABC",
    pressed: "#FA9810",
    hover: "#F8A92F",
    focus: "#FBC037",
    isLight: true,
  },
  tertiary: {
    shade4: "#05280D",
    shade3: "#0B501B",
    shade2: "#107928",
    shade1: "#16A136",
    default: "#1BC943",
    tint1: "#49D469",
    tint2: "#76DF8E",
    tint3: "#A4E9B4",
    tint4: "#D1F4D9",
  },
  white: "#FFFFFF",
  gray1: "#FAFAFA",
  neutral: {
    neutral100: "#1D1F20",
    neutral90: "#404040",
    neutral80: "#616161",
    neutral70: "#757575",
    neutral60: "#9E9E9E",
    neutral50: "#C2C2C2",
    neutral40: "#E0E0E0",
    neutral30: "#EDEDED",
    neutral20: "#FAFAFA",
    neutral10: "#FFFFFF",
  },
  success: {
    main: "#43936C",
    surface: "#F8FBF9",
    hover: "#367A59",
    border: "#43936C",
    border2: "#B8DBCA",
    pressed: "#20573D",
    container: "#F3FBFC",
  },
  danger: {
    shade2: "#870C18",
    shade1: "#B41020",
    default: "#E11428",
    tint1: "#E74353",
    tint2: "#F3A1A9",
    surface: "#FFFAFA",
    border: "#F5B1B7",

    // color codes below still from developers
    shadow: "0px 4px 20px rgba(225, 20, 40, 0.3)",
    shadow1: "0px 4px 20px rgba(180, 16, 32, 0.3)",
  },
  dangerv2: {
    default: "#E01428",
    surface: "#FFFAFA",
    border: "#F5B1B7",
    hover: "#BC1121",
    pressed: "#700A14",
    focus: "#E0142833",
  },
  warning: {
    shade2: "#978525",
    shade1: "#C9B131",
    default: "#FBDD3D",
    tint1: "#FCE464",
    tint2: "#FDEB8B",
  },
  warningv2: {
    main: "#CA7336",
    surface: "#FCF7F3",
    border: "#FEB17B",
    focus: "#CA733633",
    subtlest: "#FFFAF0",
    timestamp: "#FFC53D",
  },
  typography: {
    disabled: "#B3B3B3",
    disabled2: "rgba(76, 76, 76, 50%)",
    link: "#006DCC",
  },
  icon: {
    default: "#333333",
    weak: "#808080",
    brandVariant1: "#01959F",
    white: "#FFFFFF",
  },
  gradient: {
    gradient1: "linear-gradient(253.58deg, #02CDDB 1.55%, #1B9AAC 95.8%)",
    gradient2: "linear-gradient(253.58deg, #FFC000 1.55%, #FF8A00 95.8%)",
    gradient3: "linear-gradient(253.58deg, #F8A92F 1.55%, #FD9043 95.8%)",
    tutorCard: "linear-gradient(253.58deg, #02CDDB 1.55%, #1B9AAC 95.8%)",
  },
  gray: {
    gray80: "#333333",
    gray70: "#4C4C4C",
    gray60: "#666666",
    gray40: "#999999",
    gray30: "#B3B3B3",
    gray20: "#CCCCCC",
    gray10: "#E5E5E5",
    gray50: "#F9FAFB",
    gray300: "#D0D5DD",
    gray400: "#98A2B3",
    gray500: "#667085",
    gray600: "#475467",
    gray700: "#344054",
  },
  primitives: {
    green200: "#66D19E",
    green500: "#03703C",
  },
  background: {
    blueLight: "rgba(0, 109, 204, 0.1)",
    primary: "rgba(1, 149, 159, 0.04)",
    white40: "rgba(255, 255, 255, 0.4)",
  },
  shadow: {
    default: "0px 4px 8px 0px #0000001A",
    button: "0px 1px 2px 0px #0000001F",
    modal: "0px 4px 8px 0px #0000001A",
    shadow1: "4px 8px 24px 0px #0000003D",
    shadow2: "#0F22511A",
    bottomNav: "20px -16px 24px 0px #0000000D",
  },
  snackbar: {
    shadowSuccess: "4px 0px 0px 0px #01959F inset, 2px 4px 8px 0px #00000014",
    shadowWarning: "4px 0px 0px 0px #FD9043 inset, 2px 4px 8px 0px #00000014",
    shodowAttention: "4px 0px 0px 0px #E11428 inset, 2px 4px 8px 0px #00000014",
  },
  checkout: {
    shadowMainCard: "0px 5px 22px 0px #0F22511A",
  },
  tooltip: {
    shadow: "0px 4px 6px 0px #0F172A1A, 0px 2px 4px 0px #0F172A0F",
  },
  alumniCard: {
    shadow: "0px 4px 10px #0F22511A",
  },
  black: "#000000",
  bgProgress: "linear-gradient(253.58deg, #02CDDB 1.55%, #1B9AAC 95.8%)",
  floatingDropdownShadow: "0px 4px 4px rgba(0, 0, 0, 0.08)",
  lightOrange: "#FFF4E6",
  roseBud: "#FCB5B0",
  carnation: "#F8574D",
  deYork: "#8BC195",
  killarney: "#3E7448",
  mauve: "#F2B5FD",
  purple: "#8D00A6",
  hitPink: "#FCB383",
  crusta: "#F88B42",
  halfBaked: "#86B6D7",
  astral: "#3277A6",
  headwayBlue: "#002E6D",
};

export default Colors;
